import { CommonRuntime, Platform } from "utils/runtime";
import {
  heightLightTabIndex,
  ParseProductTagFromUrlResp,
} from "utils/playground/url";

import { TModule, TProductModule, TModuleType } from "services/page/admin";
import {
  TCombinedProductCommonExtend,
  TCombinedProductModule,
} from "services/page/modules";
import { FrontMeta } from "services/page/app";
import { ModuleInteraction } from "services/page/common";
import { getGetProductsParams } from "utils/zeusFetch";
import { fetchServerGetProducts } from "utils/serverFetch";

export const LOAD_LIMIT = 20;

/**
 * 过滤modules中前十个中的商品模块, 并发出第一个商品池子的请求, 传回UI 模块
 */
interface ReconstructFrontModsProps {
  mods: { [key: string]: TModule };
  meta: FrontMeta;
  tagQuery: ParseProductTagFromUrlResp;
  runtime: CommonRuntime;
}
export const reconstructFrontMods = async ({
  mods,
  meta,
  tagQuery,
  runtime,
}: ReconstructFrontModsProps) => {
  const nextMods = { ...mods };

  const promises: any[] = [];
  Object.values(mods).forEach((item, i: number) => {
    const itemModule = Object.keys(item.module);
    if (!itemModule || itemModule.length <= 1) {
      delete nextMods[item.id];
    }
    if (
      [TModuleType.TNewMultiProduct].includes(item.type) &&
      i < 10 &&
      item.module.proModules
    ) {
      const multiProductMod = item.module as TCombinedProductModule;
      const proModules: TProductModule[] = multiProductMod.proModules;
      const positionId = item.header.position || item.id;
      const tagIndex =
        tagQuery.modId === positionId
          ? heightLightTabIndex(proModules, tagQuery.tagName)
          : 0;
      const extend: TCombinedProductCommonExtend =
        runtime.platform === Platform.WEB
          ? multiProductMod?.pc
          : multiProductMod?.mobile;
      promises.push(
        fetchPoolForProduct({
          item: proModules?.[tagIndex],
          meta,
          extend,
          modId: item.id,
          runtime,
        })
      );
    }
  });
  const nextPool = await Promise.all(promises);
  nextPool.forEach((nextItem) => {
    if (nextMods[nextItem.moduleId]) {
      const nextMod = nextMods[nextItem.moduleId];
      const newProModule = nextMod.module.proModules || [];
      const positionId = nextMod.header.position || nextMod.id;
      const tagIndex =
        tagQuery.modId === positionId
          ? heightLightTabIndex(newProModule, tagQuery.tagName)
          : 0;

      newProModule[tagIndex] = nextItem.pool;
      nextMods[nextItem.moduleId] = {
        ...nextMod,
        module: {
          ...nextMod.module,
          proModules: [...newProModule],
        },
      };
    }
  });
  return nextMods;
};
interface FetchPoolForProductReq {
  item: Partial<TProductModule>;
  meta: FrontMeta;
  extend: TCombinedProductCommonExtend;
  modId: string;
  runtime: CommonRuntime;
}
const fetchPoolForProduct = async ({
  item,
  meta,
  extend,
  modId,
  runtime,
}: FetchPoolForProductReq): Promise<{
  moduleId: string;
  pool: TProductModule;
}> => {
  const { poolId } = item;
  const realLimit =
    extend?.layout?.interaction === ModuleInteraction.RollOver
      ? extend.maxLoad
      : Math.min(LOAD_LIMIT, extend.maxLoad);
  // 多取点数据，防止有失效的商品，超过LOAD_LIMIT的就不多取了
  const reqLimit = Math.max(realLimit, LOAD_LIMIT);

  const pool =
    (await fetchServerGetProducts(
      {
        poolId,
        offset: "0",
        limit: reqLimit.toString(),
        meta: {
          ...meta,
          userInfos: {
            ...getGetProductsParams(runtime),
          },
        },
      },
      { runtime }
    )) || {};

  // 查看server 商品池请求信息
  const products = pool?.products || [];
  const productLen = products.length;
  return {
    moduleId: modId,
    pool: {
      ...item,
      canLoadMore: !!extend.canLoadMore,
      tabExpand: extend.tabExpand,
      productElements:
        productLen > realLimit ? products.slice(0, realLimit) : products,
      maxLoad: pool?.hasNext ? extend.maxLoad : pool?.products?.length || 0,
    },
  };
};
